import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { ScrollArea } from '../ui/scroll-area'
import { ArrowLeft, Copy, ExternalLink, Code, FileJson, FileText } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { ArtifactPluginProps } from './ArtifactPluginRegistry'
import { RawJsonModal } from './common/RawJsonModal'

export const DataArtifactPlugin: React.FC<ArtifactPluginProps> = ({ transaction, onBack }) => {
  const [showRawJson, setShowRawJson] = useState(false)
  const [decodedBase64, setDecodedBase64] = useState<string | null>(null)

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`)
    }).catch(() => {
      toast.error('Failed to copy to clipboard')
    })
  }

  const formatJsonData = (data: any) => {
    return JSON.stringify(data, null, 2)
  }

  // Auto-detect JSON content in the data value
  const data = transaction.data as any
  const value = data?.value
  
  // Try to detect and parse JSON in the value
  let parsedJson: any = null
  let isJson = false
  let rawValue = value
  
  if (typeof value === 'string') {
    // First, try parsing as JSON directly
    try {
      parsedJson = JSON.parse(value)
      isJson = true
    } catch (e) {
      // Not direct JSON, try decoding as base64
      try {
        const decoded = atob(value)
        rawValue = decoded
        // Try parsing the decoded content as JSON
        try {
          parsedJson = JSON.parse(decoded)
          isJson = true
        } catch (e2) {
          // Base64 decoded but not JSON - will show as plain text
        }
      } catch (e3) {
        // Not base64 either - will show original string
      }
    }
  } else if (typeof value === 'object' && value !== null) {
    // Already a parsed object
    parsedJson = value
    isJson = true
  }

  const handleShowDecoded = () => {
    if (typeof value === 'string' && !isJson) {
      try {
        const decoded = atob(value)
        setDecodedBase64(decoded)
        toast.success('Base64 decoded successfully')
      } catch (error) {
        toast.error('Failed to decode base64')
        console.error('Base64 decode error:', error)
      }
    }
  }

  const copyJsonField = (fieldPath: string, value: any) => {
    const valueStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
    copyToClipboard(valueStr, fieldPath)
  }

  const renderJsonValue = (key: string, value: any, depth: number = 0): React.ReactNode => {
    const indent = depth * 24

    if (value === null || value === undefined) {
      return (
        <div key={key} style={{ marginLeft: `${indent}px` }} className="flex items-center space-x-2 py-1">
          <span className="text-muted-foreground font-mono text-sm">{key}:</span>
          <span className="text-muted-foreground italic text-sm">null</span>
        </div>
      )
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <div key={key} style={{ marginLeft: `${indent}px` }} className="py-1">
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-medium text-sm">{key}:</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => copyJsonField(key, value)}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <div className="border-l-2 border-muted pl-2 ml-2">
            {Object.entries(value).map(([k, v]) => renderJsonValue(k, v, depth + 1))}
          </div>
        </div>
      )
    }

    if (Array.isArray(value)) {
      return (
        <div key={key} style={{ marginLeft: `${indent}px` }} className="py-1">
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-medium text-sm">{key}:</span>
            <Badge variant="secondary" className="text-xs">Array ({value.length})</Badge>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => copyJsonField(key, value)}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <div className="border-l-2 border-muted pl-2 ml-2">
            {value.map((item, index) => renderJsonValue(`[${index}]`, item, depth + 1))}
          </div>
        </div>
      )
    }

    // Primitive value
    const valueType = typeof value
    let displayValue = String(value)
    let valueClass = 'text-sm'

    if (valueType === 'string') {
      valueClass += ' text-green-600 dark:text-green-400'
      displayValue = `"${value}"`
    } else if (valueType === 'number') {
      valueClass += ' text-blue-600 dark:text-blue-400'
    } else if (valueType === 'boolean') {
      valueClass += ' text-purple-600 dark:text-purple-400'
    }

    return (
      <div key={key} style={{ marginLeft: `${indent}px` }} className="flex items-center space-x-2 py-1">
        <span className="text-muted-foreground font-mono text-sm min-w-[200px]">{key}:</span>
        <span className={`font-mono ${valueClass} break-all flex-1`}>{displayValue}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 flex-shrink-0"
          onClick={() => copyJsonField(key, value)}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <h2 className="text-2xl font-bold">Data Transaction Details</h2>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowRawJson(true)}
          >
            <Code className="h-4 w-4 mr-2" />
            View Raw JSON
          </Button>
          <Link to={`/admin/blockchain/transactions?artifactId=${transaction.artifactId}`}>
            <Button variant="outline" size="sm">
              <ExternalLink className="h-4 w-4 mr-2" />
              View All for Artifact
            </Button>
          </Link>
        </div>
      </div>

      {/* Transaction Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Transaction Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Transaction ID</label>
              <div className="flex items-center space-x-2 mt-1">
                <p className="font-mono text-sm p-2 bg-muted rounded break-all flex-1">
                  {transaction.transactionId}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(transaction.transactionId, 'Transaction ID')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Artifact ID</label>
              <div className="flex items-center space-x-2 mt-1">
                <p className="font-mono text-sm p-2 bg-muted rounded break-all flex-1">
                  {transaction.artifactId}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(transaction.artifactId, 'Artifact ID')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Created</label>
            <p className="text-sm mt-1">
              {format(new Date(transaction.insertedAt), 'MMMM d, yyyy \'at\' HH:mm:ss')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Data Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {isJson ? (
                <FileJson className="h-5 w-5 text-green-600" />
              ) : (
                <FileText className="h-5 w-5 text-blue-600" />
              )}
              <CardTitle>Data Content</CardTitle>
              {isJson && <Badge variant="secondary">JSON</Badge>}
              {data?.type && <Badge variant="outline" className="text-xs">{data.type}</Badge>}
            </div>
            {isJson && parsedJson && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(formatJsonData(parsedJson), 'JSON Data')}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy All
              </Button>
            )}
          </div>
          <CardDescription>
            {isJson ? 'Structured JSON data' : 'Plain text content'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isJson && parsedJson ? (
            <div className="space-y-2">
              <div className="rounded-md border p-4 bg-muted/30">
                {typeof parsedJson === 'object' && !Array.isArray(parsedJson) && (
                  <div className="space-y-1">
                    {Object.entries(parsedJson).map(([key, value]) => 
                      renderJsonValue(key, value)
                    )}
                  </div>
                )}
                {Array.isArray(parsedJson) && (
                  <div className="space-y-1">
                    {parsedJson.map((item: any, index: number) => 
                      renderJsonValue(`[${index}]`, item)
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Content</label>
                  <div className="flex space-x-2">
                    {typeof value === 'string' && value.length > 100 && !decodedBase64 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleShowDecoded}
                      >
                        Try Decode Base64
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(String(rawValue || value || ''), 'Content')}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-48 w-full rounded-md border">
                  <pre className="p-4 text-sm overflow-auto whitespace-pre-wrap font-mono">
                    {String(rawValue || value || 'No content')}
                  </pre>
                </ScrollArea>
              </div>

              {decodedBase64 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Decoded Content</label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(decodedBase64, 'Decoded Data')}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                  <ScrollArea className="h-48 w-full rounded-md border">
                    <pre className="p-4 text-sm overflow-auto whitespace-pre-wrap font-mono">
                      {decodedBase64}
                    </pre>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw JSON Modal */}
      <RawJsonModal 
        transaction={transaction}
        open={showRawJson}
        onOpenChange={setShowRawJson}
      />
    </div>
  )
}
